import React, { useState, useCallback } from "react";
import { useNavigate } from "@tanstack/react-router";
import { signOut } from "@aws-amplify/auth";
import toast from "react-hot-toast";
import { useAuth } from "../contexts/AuthContext";
import { setSuppressAuthToasts } from "../utils/authStorage";
import { useIdleTimeout } from "../hooks/useIdleTimeout";
import { IdleTimeoutModal } from "./IdleTimeoutModal";

/** 15 minutes for OFFICIAL-SENSITIVE (NCSC recommendation). */
const IDLE_THRESHOLD_MS = 15 * 60 * 1000;
/** Show warning 5 minutes before auto-logout. */
const WARNING_BEFORE_MS = 5 * 60 * 1000;

interface IdleTimeoutWrapperProps {
  children: React.ReactNode;
}

/**
 * Wraps protected content and enforces idle session timeout:
 * activity tracking, T-5 min warning modal, auto-logout at T-0, visibilitychange for background tabs.
 */
export const IdleTimeoutWrapper: React.FC<IdleTimeoutWrapperProps> = ({
  children,
}) => {
  const navigate = useNavigate();
  const { resetAuth } = useAuth();
  const [showIdleWarning, setShowIdleWarning] = useState(false);
  const [warningRemainingMs, setWarningRemainingMs] = useState(0);

  const handleLogout = useCallback(() => {
    setShowIdleWarning(false);
    setSuppressAuthToasts(true);
    signOut()
      .then(() => {
        resetAuth();
        toast.error("You have been logged out due to inactivity.");
        navigate({ to: "/login", replace: true });
      })
      .catch(() => {
        resetAuth();
        navigate({ to: "/login", replace: true });
      });
  }, [resetAuth, navigate]);

  const { reset: resetIdle } = useIdleTimeout({
    enabled: true,
    idleThresholdMs: IDLE_THRESHOLD_MS,
    warningBeforeMs: WARNING_BEFORE_MS,
    onWarning: (remainingMs) => {
      setWarningRemainingMs(remainingMs);
      setShowIdleWarning(true);
    },
    onLogout: handleLogout,
  });

  const handleStayLoggedIn = useCallback(() => {
    resetIdle();
    setShowIdleWarning(false);
  }, [resetIdle]);

  return (
    <>
      <IdleTimeoutModal
        open={showIdleWarning}
        remainingMs={warningRemainingMs}
        onStayLoggedIn={handleStayLoggedIn}
      />
      {children}
    </>
  );
};
